import {
  createMessagingSessionApi,
  createStaffConversationContextApi,
  createStaffHandoffsApi,
  createStaffInboxApi,
} from "@lafayette/api-client";
import type {
  HandoffAvailabilityOverride,
  HandoffEscalationReason,
  StaffConversationContext,
  StaffConversationJoinResult,
  StaffHandoffCommandResult,
  StaffInboxFilter,
  StaffInboxPage,
  StaffMessagingSession,
} from "@lafayette/schemas";

// The API the workspace calls (CHT-732). ONE interface, so the pages never know which client sits behind it and a test swaps in a fake
// (`test/fake-conversations-api.ts`). Every method here is a real, typed client of `@lafayette/api-client`: the context and join clients
// (this lane), the Stream session client (lane A), and the handoff command and inbox clients (lane C: `createStaffHandoffsApi`,
// `createStaffInboxApi`). The stand-in of the first version of this file is gone. A failure of any of them is an error of the api-client
// package, so the pages read it with `classifyFailure` (one shape) and need no family of their own (CHT-1992).

export type ConversationsCallOptions = Readonly<{ signal?: AbortSignal }>;
export type ConversationsCommandOptions = ConversationsCallOptions &
  Readonly<{
    /** One key per user action. A retry of the same action sends the SAME key, so the server runs it once. */
    idempotencyKey: string;
  }>;

export type InboxQuery = Readonly<{ filter: StaffInboxFilter; cursor?: string; limit?: number }>;

export interface ConversationsApi {
  /** MC-C6. One page of the inbox, in the order the server gives (attention first). `EXCEPTIONS` is for supervisors only (403 otherwise). */
  listInbox(query: InboxQuery, options?: ConversationsCallOptions): Promise<StaffInboxPage>;
  /** MC-E1. */
  getContext(
    conversationId: string,
    options?: ConversationsCallOptions,
  ): Promise<StaffConversationContext>;
  /** MC-E2. */
  join(
    conversationId: string,
    options?: ConversationsCallOptions,
  ): Promise<StaffConversationJoinResult>;
  /** MC-C1. */
  acceptHandoff(
    handoffId: string,
    body: Readonly<{ expectedVersion: number }>,
    options: ConversationsCommandOptions,
  ): Promise<StaffHandoffCommandResult>;
  /**
   * MC-C2. WAITING: a new target. ACCEPTED: a new owner. `availabilityOverride` is a supervisor's reason for a colleague who set
   * themselves unavailable; without it the server refuses that target (409 `handoff.target_unavailable`).
   */
  reassignHandoff(
    handoffId: string,
    body: Readonly<{
      expectedVersion: number;
      targetStaffUserId: string;
      reason: string;
      availabilityOverride?: HandoffAvailabilityOverride;
    }>,
    options: ConversationsCommandOptions,
  ): Promise<StaffHandoffCommandResult>;
  /** MC-C3. */
  releaseHandoff(
    handoffId: string,
    body: Readonly<{ expectedVersion: number; reason: string }>,
    options: ConversationsCommandOptions,
  ): Promise<StaffHandoffCommandResult>;
  /** MC-C4. Supervisor only. */
  cancelHandoff(
    handoffId: string,
    body: Readonly<{ expectedVersion: number; reason: string }>,
    options: ConversationsCommandOptions,
  ): Promise<StaffHandoffCommandResult>;
  /** MC-C5. A supervisor or the current target moves a WAITING handoff to the next coverage, with a reason code. */
  escalateHandoff(
    handoffId: string,
    body: Readonly<{ expectedVersion: number; reasonCode: HandoffEscalationReason }>,
    options: ConversationsCommandOptions,
  ): Promise<StaffHandoffCommandResult>;
  /** MC-A2. The Stream token of this staff member. Also the token refresh of the connection. */
  openMessagingSession(options?: ConversationsCallOptions): Promise<StaffMessagingSession>;
}

// --- the HTTP implementation ------------------------------------------------------------------------------------------

export type HttpConversationsApiOptions = Readonly<{
  baseUrl: string;
  getAccessToken: () => string | null | undefined | Promise<string | null | undefined>;
  fetch?: typeof globalThis.fetch;
}>;

export function createHttpConversationsApi(options: HttpConversationsApiOptions): ConversationsApi {
  const context = createStaffConversationContextApi(options);
  const sessions = createMessagingSessionApi(options);
  const handoffs = createStaffHandoffsApi(options);
  const inbox = createStaffInboxApi(options);

  return {
    listInbox: (query, callOptions) => inbox.list(query, callOptions),
    getContext: (conversationId, callOptions) => context.getContext(conversationId, callOptions),
    join: (conversationId, callOptions) => context.join(conversationId, callOptions),
    acceptHandoff: (handoffId, body, commandOptions) =>
      handoffs.accept(handoffId, body, commandOptions),
    reassignHandoff: (handoffId, body, commandOptions) =>
      handoffs.reassign(handoffId, body, commandOptions),
    releaseHandoff: (handoffId, body, commandOptions) =>
      handoffs.release(handoffId, body, commandOptions),
    cancelHandoff: (handoffId, body, commandOptions) =>
      handoffs.cancel(handoffId, body, commandOptions),
    escalateHandoff: (handoffId, body, commandOptions) =>
      handoffs.escalate(handoffId, body, commandOptions),
    openMessagingSession: (callOptions) => sessions.openStaffSession(callOptions),
  };
}
